import type { SearchResponse, SearchTab } from "@/types/v2/search"

// Fetch layer for the command palette (Contract A — CON-183).
//
// `GET /v2/searches?q=<query>&type=<tab>` returns plain
// camelCase JSON (see `SearchResponse`). This module owns the
// raw fetch plus the request-cancellation discipline the
// palette needs: every keystroke supersedes the previous
// in-flight request, so a slow earlier response can never
// clobber the input the user is now looking at.

export const SEARCH_ENDPOINT = "/v2/searches"

// Build the request URL for a query + tab filter. Exported
// so tests (and the backend teammate) can assert the exact
// shape without reaching into the fetch.
export function buildSearchUrl(query: string, type: SearchTab): string {
  const params = new URLSearchParams({ q: query, type })
  return `${SEARCH_ENDPOINT}?${params.toString()}`
}

export interface FetchSearchOptions {
  signal?: AbortSignal
}

// Perform a single search request. Throws on a non-2xx
// response or an aborted signal (the standard fetch
// contract) — callers decide how to surface that. The
// palette treats an `AbortError` as "superseded, ignore"
// and any other error as a soft empty state.
export async function fetchSearch(
  query: string,
  type: SearchTab,
  { signal }: FetchSearchOptions = {},
): Promise<SearchResponse> {
  const response = await fetch(buildSearchUrl(query, type), {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "same-origin",
    signal,
  })

  if (!response.ok) {
    throw new Error(`Search request failed: ${response.status}`)
  }

  return (await response.json()) as SearchResponse
}

export const SEARCH_DEBOUNCE_MS = 200

// A small debounced-search driver, framework-agnostic so it
// can be unit-tested without React. The palette wraps it in a
// hook (`useDebouncedSearch`) but the scheduling +
// abort-previous logic lives here where it's easy to test
// with fake timers.
//
// `run(query, type)` schedules a fetch after the debounce
// window; a newer `run` before the window elapses cancels the
// pending timer AND aborts any request already in flight.
// `cancel()` tears everything down (call on unmount / close).
export interface SearchRunner {
  run: (query: string, type: SearchTab) => void
  cancel: () => void
}

export interface CreateSearchRunnerOptions {
  onResult: (result: SearchResponse) => void
  onError?: (error: unknown) => void
  onStart?: () => void
  debounceMs?: number
  // Injectable for tests; defaults to the real fetch wrapper.
  fetcher?: typeof fetchSearch
}

export function createSearchRunner({
  onResult,
  onError,
  onStart,
  debounceMs = SEARCH_DEBOUNCE_MS,
  fetcher = fetchSearch,
}: CreateSearchRunnerOptions): SearchRunner {
  let timer: ReturnType<typeof setTimeout> | null = null
  let controller: AbortController | null = null

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
  }

  const abortInFlight = () => {
    if (controller) {
      controller.abort()
      controller = null
    }
  }

  const run = (query: string, type: SearchTab) => {
    // Supersede anything pending or in flight.
    clearTimer()
    abortInFlight()

    timer = setTimeout(() => {
      timer = null
      const localController = new AbortController()
      controller = localController
      onStart?.()

      fetcher(query, type, { signal: localController.signal })
        .then((result) => {
          // Ignore a response that a newer run already
          // superseded (its controller replaced ours).
          if (controller !== localController) return
          controller = null
          onResult(result)
        })
        .catch((error) => {
          if (isAbortError(error)) return
          if (controller === localController) controller = null
          onError?.(error)
        })
    }, debounceMs)
  }

  const cancel = () => {
    clearTimer()
    abortInFlight()
  }

  return { run, cancel }
}

// `fetch` aborts surface as a DOMException named
// "AbortError"; some polyfills throw a plain Error with that
// name. Match either.
export function isAbortError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === "AbortError" ||
      (typeof DOMException !== "undefined" &&
        error instanceof DOMException &&
        error.name === "AbortError"))
  )
}
